<template>
  <div
    v-if="block"
    class="absolute z-10 flex items-center gap-0.5 transition-[top] duration-75"
    :style="{ top: `${block.top}px`, left: '12px' }"
    @mouseleave="onLeave"
  >
    <button type="button" class="flex h-7 w-7 items-center justify-center rounded-md text-[#1c4848]/40 hover:bg-[#1c4848]/5 hover:text-[#1c4848]" aria-label="Add a block below" title="Add a block below" @click="addBelow">
      <iconify-icon icon="solar:add-circle-linear" class="text-lg" aria-hidden="true" />
    </button>
    <button
      type="button"
      draggable="true"
      class="flex h-7 w-6 cursor-grab items-center justify-center rounded-md text-[#1c4848]/40 hover:bg-[#1c4848]/5 hover:text-[#1c4848] active:cursor-grabbing"
      aria-label="Drag to move this block"
      title="Drag to move"
      @dragstart="onDragStart"
      @dragend="block = null"
      @click="selectBlock"
    >
      <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor" aria-hidden="true"><circle cx="2" cy="2" r="1.5"/><circle cx="8" cy="2" r="1.5"/><circle cx="2" cy="8" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="2" cy="14" r="1.5"/><circle cx="8" cy="14" r="1.5"/></svg>
    </button>
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { NodeSelection } from '@tiptap/pm/state';

/** The handle beside the block under the mouse: drag it to move the block, + to add one below. */
const props = defineProps({
  editor: { type: Object, required: true },
  paper: { type: Object, required: true },
});

const block = ref(null);

function blockAt(y) {
  const { view, state } = props.editor;
  const dom = view.dom.getBoundingClientRect();
  const found = view.posAtCoords({ left: dom.left + dom.width / 2, top: y });
  if (!found) return null;
  const inside = found.inside >= 0 ? found.inside : found.pos;
  const $pos = state.doc.resolve(Math.min(inside, state.doc.content.size));
  const pos = $pos.depth > 0 ? $pos.before(1) : inside;
  const node = state.doc.nodeAt(pos);
  if (!node) return null;
  const element = view.nodeDOM(pos);
  return element instanceof HTMLElement ? { pos, node, element } : null;
}

function onMove(event) {
  if (!props.editor.isEditable) return;
  const found = blockAt(event.clientY);
  if (!found) return;
  block.value = {
    ...found,
    top: found.element.getBoundingClientRect().top - props.paper.getBoundingClientRect().top,
  };
}

function onLeave(event) {
  if (!props.paper.contains(event.relatedTarget)) block.value = null;
}

function selectBlock() {
  const { view, state } = props.editor;
  view.dispatch(state.tr.setSelection(NodeSelection.create(state.doc, block.value.pos)));
  view.focus();
}

function onDragStart(event) {
  const { view, state } = props.editor;
  const selection = NodeSelection.create(state.doc, block.value.pos);
  view.dispatch(state.tr.setSelection(selection));
  event.dataTransfer.effectAllowed = 'move';
  event.dataTransfer.setDragImage(block.value.element, 0, 0);
  const { dom, text } = view.serializeForClipboard(selection.content());
  event.dataTransfer.setData('text/html', dom.innerHTML);
  event.dataTransfer.setData('text/plain', text);
  view.dragging = { slice: selection.content(), move: true };
}

function addBelow() {
  const after = block.value.pos + block.value.node.nodeSize;
  props.editor.chain().insertContentAt(after, { type: 'paragraph' }).focus(after + 1).insertContent('/').run();
}

onMounted(() => {
  props.paper.addEventListener('mousemove', onMove);
  props.paper.addEventListener('mouseleave', onLeave);
});
onBeforeUnmount(() => {
  props.paper.removeEventListener('mousemove', onMove);
  props.paper.removeEventListener('mouseleave', onLeave);
});
</script>
